import React, { useState, useEffect } from "react";
import "./SolicitarAutor.css";

const SolicitarAutor = ({ userData, loading: parentLoading }) => {
    const [justificativa, setJustificativa] = useState("");
    const [loading, setLoading] = useState(false);
    const [solicitacoes, setSolicitacoes] = useState([]);
    const [loadingSolicitacoes, setLoadingSolicitacoes] = useState(true);
    const API_URL = process.env.REACT_APP_API;

    const isAuthor = ["autor", "admin"].includes(userData?.permissao);

    useEffect(() => {
        // Só carregar solicitações se não estiver carregando do parent e não for autor
        if (!parentLoading && !isAuthor && userData) {
            buscarSolicitacoes();
        } else if (!parentLoading) {
            setLoadingSolicitacoes(false);
        }
    }, [parentLoading, isAuthor, userData]);

    const buscarSolicitacoes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}api/usuario-editora/autor-solicitacoes`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSolicitacoes(data);
            }
        } catch (error) {
            console.error('Erro ao buscar solicitações:', error);
        } finally {
            setLoadingSolicitacoes(false);
        }
    };

    const enviarSolicitacao = async (e) => {
        e.preventDefault();
        
        if (justificativa.length < 10) {
            alert('A justificativa deve ter pelo menos 10 caracteres.');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}api/usuario-editora/autor-solicitacoes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ justificativa })
            });

            const data = await response.json();

            if (response.ok) {
                alert('Solicitação enviada com sucesso! Aguarde a avaliação da equipe.');
                setJustificativa('');
                buscarSolicitacoes();
            } else {
                alert(data.error || 'Erro ao enviar solicitação');
            }
        } catch (error) {
            console.error('Erro ao enviar solicitação:', error);
            alert('Erro ao enviar solicitação. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pendente': { class: 'status-pendente', text: '⏳ Pendente' },
            'aprovado': { class: 'status-aprovado', text: '✅ Aprovado' },
            'rejeitado': { class: 'status-rejeitado', text: '❌ Rejeitado' }
        };
        
        const config = statusConfig[status] || statusConfig['pendente'];
        return <span className={`status-badge ${config.class}`}>{config.text}</span>;
    };

    // Se ainda está carregando dados do parent
    if (parentLoading) {
        return (
            <div className="solicitar-autor-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando dados do usuário...</p>
                </div>
            </div>
        );
    }

    // Se já é autor
    if (isAuthor) {
        return (
            <div className="solicitar-autor-container">
                <h2>Solicitar ser Autor</h2>
                <div className="already-author">
                    <div className="success-message">
                        <span className="success-icon">✅</span>
                        <div>
                            <h3>Você já é um Autor!</h3>
                            <p>Parabéns! Você já tem permissão de autor na plataforma.</p>
                            <p>Acesse as outras abas do perfil para gerenciar seus livros, vendas e configurações.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const temSolicitacaoPendente = solicitacoes.some(s => s.status === 'pendente');

    return (
        <div className="solicitar-autor-container">
            <h2>Solicitar ser Autor</h2>
            
            <div className="info-section">
                <div className="info-card">
                    <h3>💡 Benefícios de ser Autor</h3>
                    <ul>
                        <li>📚 Publique seus próprios livros na plataforma</li>
                        <li>💰 Receba uma porcentagem das vendas dos seus livros</li>
                        <li>🎯 Desconto automático ao comprar seus próprios livros</li>
                        <li>📊 Acesse relatórios detalhados de vendas</li>
                        <li>✏️ Gerencie seus dados de autor (biografia, redes sociais)</li>
                        <li>🏦 Configure recebimento direto via Pagar.me</li>
                    </ul>
                </div>
            </div>

            {!temSolicitacaoPendente && (
                <div className="form-section">
                    <h3>Nova Solicitação</h3>
                    <form onSubmit={enviarSolicitacao} className="solicitation-form">
                        <div className="form-group">
                            <label htmlFor="justificativa">
                                Por que você deseja se tornar um autor na plataforma?
                            </label>
                            <textarea
                                id="justificativa"
                                value={justificativa}
                                onChange={(e) => setJustificativa(e.target.value)}
                                placeholder="Descreva sua experiência, qualificações, livros que pretende publicar, etc. (mínimo 10 caracteres)"
                                rows={6}
                                required
                                minLength={10}
                                maxLength={1000}
                            />
                            <small>{justificativa.length}/1000 caracteres</small>
                        </div>
                        
                        <button 
                            type="submit" 
                            disabled={loading || justificativa.length < 10}
                            className="btn-submit"
                        >
                            {loading ? '⏳ Enviando...' : '📤 Enviar Solicitação'}
                        </button>
                    </form>
                </div>
            )}

            <div className="solicitacoes-section">
                <h3>Minhas Solicitações</h3>
                
                {loadingSolicitacoes ? (
                    <div className="loading">⏳ Carregando solicitações...</div>
                ) : solicitacoes.length === 0 ? (
                    <div className="empty-state">
                        📝 Você ainda não fez nenhuma solicitação para ser autor.
                    </div>
                ) : (
                    <div className="solicitacoes-list">
                        {solicitacoes.map((solicitacao) => (
                            <div key={solicitacao.id} className="solicitacao-card">
                                <div className="solicitacao-header">
                                    <span className="solicitacao-date">
                                        📅 {new Date(solicitacao.created_at).toLocaleDateString('pt-BR')}
                                    </span>
                                    {getStatusBadge(solicitacao.status)}
                                </div>
                                
                                <div className="solicitacao-content">
                                    <p><strong>Justificativa:</strong></p>
                                    <p>{solicitacao.justificativa}</p>
                                </div>

                                {solicitacao.status === 'rejeitado' && solicitacao.motivo_rejeicao && (
                                    <div className="rejeicao-motivo">
                                        <p><strong>❌ Motivo da rejeição:</strong></p>
                                        <p>{solicitacao.motivo_rejeicao}</p>
                                    </div>
                                )}

                                {solicitacao.data_avaliacao && (
                                    <div className="avaliacao-info">
                                        <small>
                                            Avaliado em: {new Date(solicitacao.data_avaliacao).toLocaleDateString('pt-BR')}
                                            {solicitacao.avaliador && ` por ${solicitacao.avaliador.nome}`}
                                        </small>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SolicitarAutor; 